// web-frontend/src/routes/index.jsx
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../shared/stores/authStore';
// 🔥 [2026-05-21] 온보딩 라우팅: VISITOR/GUEST 는 SPC 큐레이션, COMPANY/BUYER 는 풀 비즈니스
import { isBusinessUser, isCurationOnlyUser } from '../shared/utils/userPermissions';
// 🔥 [2026-06-26] 탄소포인트 미사용 시 /carbon 라우트 자체를 등록하지 않음(직접 URL 차단)
import { isFeatureEnabled } from '../shared/config/tenant';

// Layout imports
import MainLayout from '../layouts/MainLayout';
import AuthLayout from '../layouts/AuthLayout';

// 🔥 RouteGuard import (필요한 경우에만 사용)
import RouteGuard from '../components/auth/RouteGuard';

// Lazy load pages
const HomePage = lazy(() => import('../pages/HomePage'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const CompaniesPage = lazy(() => import('../pages/CompaniesPage'));
const CompanyDetailPage = lazy(() => import('../pages/CompanyDetailPage'));
const ChatRoom = lazy(() => import('../pages/ChatRoom'));

// 🔥 ActivitiesPage를 채팅 목록 페이지로 사용 (파일명은 유지하되 역할 변경)
const ActivitiesPage = lazy(() => import('../pages/ActivitiesPage'));
const EventsPage = lazy(() => import('../pages/EventsPage'));

// 🔥🔥🔥 [신규 추가] 이벤트 상세 페이지 import
const EventDetailPage = lazy(() => import('../pages/EventDetailPage'));

const ProfilePage = lazy(() => import('../pages/ProfilePage'));

// 🔥 매칭 페이지
const MatchingPage = lazy(() => import('../pages/MatchingPage'));
const ParticipantHistoryPage = lazy(() => import('../pages/ParticipantHistoryPage'));

// 🔥 미팅 관련 페이지들
const MeetingsPage = lazy(() => import('../pages/MeetingsPage'));
const MeetingDetailPage = lazy(() => import('../pages/MeetingDetailPage'));

// 🔥 설문 페이지들
const SurveyIntroPage = lazy(() => import('../pages/survey/SurveyIntroPage'));
const SurveyPage = lazy(() => import('../pages/survey/SurveyPage'));

// 🔥🔥🔥 [신규 추가] 비밀번호 변경 페이지
const ChangePasswordPage = lazy(() => import('../pages/auth/PasswordOnlyPage'));

// 🔥🔥🔥 [신규 추가] 비밀번호 재설정 페이지 (토큰 기반)
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));

// 📧 [2026-05-24 신규] 이메일 인증 페이지
const VerifyEmailPage = lazy(() => import('../pages/auth/VerifyEmailPage'));

// 🔥 [신규 추가] 프로필 하위 페이지들
const MyInfoPage = lazy(() => import('../pages/profile/MyInfoPage'));
const CompanyInfoPage = lazy(() => import('../pages/profile/CompanyInfoPage'));
const CarbonActivitiesPage = lazy(() => import('../pages/profile/CarbonActivitiesPage'));
const BookmarksPage = lazy(() => import('../pages/profile/BookmarksPage'));
const NotificationSettingsPage = lazy(() => import('../pages/profile/NotificationSettingsPage'));
const AppSettingsPage = lazy(() => import('../pages/profile/AppSettingsPage'));

// 🔥🔥🔥 [신규 추가] 부스 맵 페이지
const BoothMapPage = lazy(() => import('../pages/BoothMapPage'));

// 🔥 [2026-05-21] 큐레이션 페이지 (SPC 모드)
const CurationPage = lazy(() => import('../pages/CurationPage'));

// 🌉 [2026-05-24] 외부 SPC → 메인 웹앱 진입 브릿지
const WelcomePage = lazy(() => import('../pages/WelcomePage'));

// Loading component
const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: 'var(--app-splash-bg, var(--color-bg-primary, #F0F4FA))',
    }}
  >
    <div
      style={{
        width: '50px',
        height: '50px',
        border: '5px solid var(--color-border-primary, #E2E8F0)',
        borderTop: '5px solid var(--color-secondary, #fbba21)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    ></div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// 🔥 [2026-05-21] 비즈니스 권한 가드. 큐레이션 전용 사용자가 비즈니스 전용 페이지(matching/chats/meetings)에
// 직접 URL 로 접근하면 홈(/)으로 보낸다. (2026-06-16: 기존 /curation 자동 리다이렉트 제거 — 사용자 요청.
//  UI 클릭은 MainLayout.handleNavigate 가 모달로 안내하므로 이 가드는 직접 URL 접근용 폴백이다.)
const BusinessRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isCurationOnlyUser(user)) return <Navigate to="/" replace />;
  return children;
};

// 루트(/) — 모든 로그인 사용자는 비즈니스 HomePage 를 홈으로 본다 (2026-06-16, 사용자 요청).
// (이전: 큐레이션 전용 사용자[VISITOR/GUEST/미승인 BUSINESS_VISITOR]는 /curation 으로 자동 리다이렉트했으나,
//  "홈이 기존 웹앱 느낌이 안 난다"는 피드백으로 제거 — 큐레이션은 이제 명시적 진입으로만.)
const HomeOrCuration = ({ businessChildren }) => {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return businessChildren;
};

// Guest Route wrapper
const GuestRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  if (isAuthenticated && user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* 🔐 인증 관련 라우트 */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </GuestRoute>
          }
        />
        
        <Route
          path="/register"
          element={
            <GuestRoute>
              <AuthLayout>
                <RegisterPage />
              </AuthLayout>
            </GuestRoute>
          }
        />
        
        {/* 🔥🔥🔥 [신규] 비밀번호 변경 페이지 (로그인 상태여야 접근 가능) */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <AuthLayout>
                <ChangePasswordPage />
              </AuthLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥🔥🔥 [신규] 비밀번호 재설정 페이지 (토큰 기반 - 비로그인 상태에서 접근) */}
        <Route
          path="/reset-password"
          element={
            <GuestRoute>
              <AuthLayout>
                <ResetPasswordPage />
              </AuthLayout>
            </GuestRoute>
          }
        />

        {/* 🔥 설문 인트로 라우트 */}
        <Route
          path="/survey-intro"
          element={
            <ProtectedRoute>
              <SurveyIntroPage />
            </ProtectedRoute>
          }
        />
        
        {/* 🔥🔥🔥 [수정] 설문 라우트 - slug 파라미터 지원 */}
        <Route
          path="/survey/:slug"
          element={
            <ProtectedRoute>
              <SurveyPage />
            </ProtectedRoute>
          }
        />
        
        {/* 🔥 설문 기본 라우트 (slug 없이 접근 시) - 폴백용 */}
        <Route
          path="/survey"
          element={
            <ProtectedRoute>
              <SurveyPage />
            </ProtectedRoute>
          }
        />

        {/* 🏠 메인 — 모든 로그인 사용자 홈 = 비즈니스 HomePage (2026-06-16) */}
        <Route
          path="/"
          element={
            <HomeOrCuration businessChildren={
              <MainLayout>
                <HomePage />
              </MainLayout>
            } />
          }
        />

        {/* 🎯 큐레이션 모드 (SPC) — VISITOR/GUEST/승인전 BUSINESS_VISITOR */}
        <Route
          path="/curation"
          element={
            <ProtectedRoute>
              <CurationPage />
            </ProtectedRoute>
          }
        />

        {/* 🌉 외부 SPC 진입 브릿지 (2026-05-24) — 미로그인도 접근 가능 */}
        <Route path="/welcome" element={<WelcomePage />} />

        {/* 📧 이메일 인증 (2026-05-24) — 토큰 URL 클릭 시 접근, 미로그인도 가능 */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* 🔥 매칭 — 비즈니스 전용 */}
        <Route
          path="/matching"
          element={
            <BusinessRoute>
              <MainLayout>
                <MatchingPage />
              </MainLayout>
            </BusinessRoute>
          }
        />

        <Route path="/records" element={
          <ProtectedRoute><MainLayout><ParticipantHistoryPage /></MainLayout></ProtectedRoute>
        } />

        {/* 💬 채팅 목록 — 비즈니스 전용 */}
        <Route
          path="/chats"
          element={
            <BusinessRoute>
              <MainLayout>
                <ActivitiesPage />
              </MainLayout>
            </BusinessRoute>
          }
        />

        {/* 💬 채팅방 상세 — 비즈니스 전용 */}
        <Route
          path="/chats/:chatId"
          element={
            <BusinessRoute>
              <MainLayout>
                <ChatRoom />
              </MainLayout>
            </BusinessRoute>
          }
        />

        {/* 🏢 기업 관련 라우트 */}
        <Route
          path="/companies"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CompaniesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/companies/:companyId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CompanyDetailPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥 일정 페이지 */}
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EventsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥🔥🔥 [신규 추가] 이벤트 상세 페이지 라우트 */}
        <Route
          path="/events/:eventId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EventDetailPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥 미팅 — 비즈니스 전용 */}
        <Route
          path="/meetings"
          element={
            <BusinessRoute>
              <MainLayout>
                <MeetingsPage />
              </MainLayout>
            </BusinessRoute>
          }
        />

        <Route
          path="/meetings/:meetingId"
          element={
            <BusinessRoute>
              <MainLayout>
                <MeetingDetailPage />
              </MainLayout>
            </BusinessRoute>
          }
        />

        {/* 🔥 프로필 메인 페이지 */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥 [신규] 프로필 하위 기능 - 내 정보 수정 */}
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyInfoPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥 [신규] 프로필 하위 기능 - 회사 정보 */}
        <Route
          path="/profile/company"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CompanyInfoPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥 [신규] 프로필 하위 기능 - 북마크 통합 */}
        <Route
          path="/profile/bookmarks"
          element={
            <ProtectedRoute>
              <MainLayout>
                <BookmarksPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥 [신규] 프로필 하위 기능 - 알림 설정 */}
        <Route
          path="/profile/notifications"
          element={
            <ProtectedRoute>
              <MainLayout>
                <NotificationSettingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🔥 [신규] 프로필 하위 기능 - 앱 설정 */}
        <Route
          path="/profile/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AppSettingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 🌱 탄소 활동 페이지 — 🔥 [2026-06-26] carbon 미사용 시 라우트 미등록 → '*' 캐치올로 홈 리다이렉트 */}
        {isFeatureEnabled('carbon') && (
        <Route
          path="/carbon"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CarbonActivitiesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        )}

        {/* 🗺️🔥🔥🔥 [신규 추가] 부스 맵 페이지 라우트 */}
        <Route
          path="/map"
          element={
            <ProtectedRoute>
              <MainLayout>
                <BoothMapPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 404 Not Found - 홈으로 리다이렉트 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
